import { ChangeDetectionStrategy, Component, inject, OnInit, signal, ViewEncapsulation } from '@angular/core';
import { CapturumButtonModule } from '@capturum/ui/button';
import { HasChildrenPipe } from '@core/directives/has-children.pipe';
import { Parameter } from '@features/applications/interfaces/action.interface';
import { FieldArrayType, FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AddChildAllowedPipe } from '@shared/pipes/add-child-allowed.pipe';
import { CommonService } from '@shared/services/common.service';
import { FormlyTypeService } from '@shared/services/formly-type.service';
import { ConfirmationService } from 'primeng/api';
import { TooltipModule } from 'primeng/tooltip';

@Component({
  selector: 'app-formly-recursive-parameter-type',
  imports: [FormlyModule, CapturumButtonModule, TranslateModule, HasChildrenPipe, TooltipModule, AddChildAllowedPipe],
  templateUrl: './formly-recursive-parameter-type.component.html',
  styleUrl: './formly-recursive-parameter-type.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormlyRecursiveParameterTypeComponent extends FieldArrayType implements OnInit {
  applicationId: string;
  actionId: string;
  collapsed = signal<boolean>(false);

  #confirmationService = inject(ConfirmationService);
  #translateService = inject(TranslateService);
  #commonService = inject(CommonService);
  #formlyTypeService = inject(FormlyTypeService);

  get addDisabled(): boolean {
    return this.#commonService.getAddButtonDisabled();
  }

  get triggerCheckValue(): number {
    return this.#formlyTypeService.getTriggerCheck();
  }

  ngOnInit(): void {
    this.applicationId = this.props.applicationId;
    this.actionId = this.props.actionId;

    if (!this.props.hideButtons) {
      this.formControl.disable();
    }

    if (this.props.isRoot) {
      this.formControl.controls.forEach((control) => {
        control.patchValue({
          is_root: true,
        });
      });
    }
  }

  removeField(index: number, field: FormlyFieldConfig): void {
    this.#confirmationService.confirm({
      header: this.#translateService.instant('confirm.title'),
      message: this.#translateService.instant('confirm.entity.delete.confirmation', {}),
      accept: () => {
        if (this.props.onRemove) {
          const formControl = this.formControl.at(index);
          const id = formControl.get('id')?.value;

          this.props.onRemove(id, this.field, formControl, () => {
            this.#formlyTypeService.setTriggerCheck(this.#getRandom());
            this.#triggerAddChildAllowedPipe(field);
            super.remove(index);
          });
        } else {
          super.remove(index);
        }
      },
    });
  }

  override add(): void {
    const index = this.field.fieldGroup.length;

    if (this.props.onAdd) {
      super.add(index);

      this.props.onAdd(this.field, this.formControl, index, () => {
        this.formControl.at(index).patchValue({
          is_edit: true,
        });
        this.#commonService.setAddButtonDisabled(true);
      });
    } else {
      const parentId = this.props.isRoot ? null : this.form.get('id')?.value;

      super.add(index);

      this.formControl.at(index).patchValue({
        is_edit: true,
        parent_id: parentId,
        ...(this.props.isRoot ? { is_root: true } : {}),
      });
      this.#commonService.setAddButtonDisabled(true);
    }
  }

  edit(index: number, field?: FormlyFieldConfig): void {
    this.#triggerAddChildAllowedPipe(field);
    this.#formlyTypeService.setTriggerCheck(this.#getRandom());

    if (this.props.onEdit) {
      this.props.onEdit(index, this.field);
    } else {
      this.#commonService.setAddButtonDisabled(true);
      this.formControl.at(index).enable();
      this.formControl.at(index).patchValue({
        is_edit: true,
      });

      for (const control of this.formControl.controls) {
        if (control !== this.formControl.at(index)) {
          control.disable();
          control.patchValue({
            is_edit: false,
          });
        }
      }
    }
  }

  save(index: number, field?: FormlyFieldConfig): void {
    this.#triggerAddChildAllowedPipe(field);
    this.#formlyTypeService.setTriggerCheck(this.#getRandom());

    if (this.props.onSave) {
      const formControl = this.formControl.at(index);

      this.props.onSave(index, formControl, (parameter: Parameter) => {
        this.#commonService.setAddButtonDisabled(false);
        formControl.patchValue({
          is_edit: false,
          ...parameter,
        });
        formControl.disable();
      });
    }
  }

  cancel(index: number): void {
    if (this.props.onCancel) {
      this.props.onCancel(index, this.field, this.formControl);
    } else {
      if (this.formControl.at(index).get('id')?.value) {
        this.formControl.at(index).disable();
        this.formControl.at(index).patchValue({
          is_edit: false,
        });
      } else {
        super.remove(index);
      }

      this.#commonService.setAddButtonDisabled(false);
    }
  }

  toggleCollapse(index: number): void {
    this.field.fieldGroup[index].props.collapsed = !this.field.fieldGroup[index].props.collapsed;
  }

  #getRandom(): number {
    return Math.random() * 10;
  }

  #triggerAddChildAllowedPipe(formlyField: FormlyFieldConfig): void {
    // This method is used to trigger the AddChildAllowedPipe
    // Because the pipe is used in the template and pure, it will not get triggered on edit array item.
    // We need to ensure it gets triggered
    const fieldGroup = formlyField?.fieldGroup || [];

    for (const field of fieldGroup) {
      field.props.is_edit = this.#getRandom();
    }
  }
}
